import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Capacitor } from '@capacitor/core'
import { doc, onSnapshot } from 'firebase/firestore'
import type { UserProfile } from '../../types/schema'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import ForumRoundedIcon from '@mui/icons-material/ForumRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded'
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import WorkspacesRoundedIcon from '@mui/icons-material/WorkspacesRounded'
import { useAuth } from '../../context/useAuth'
import { useClientPreferences } from '../../context/useClientPreferences'
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences'
import { useWorkspace } from '../../context/useWorkspace'
import type { DisplayPreferenceType } from '../../types/schema'
import { computeDisplayName, helperUsernameFor, normalizeAccountRole, normalizeUsername, validateUsername } from '../../utils/userIdentity'
import { isHelperOnlyAccount } from '../../utils/helperMembership'
import { useDisplayName } from '../../hooks/useDisplayName'
import { updateUserProfile, updateUserProfileImage } from '../../services/userProfileService'
import { confirmPhoneChangeCode, finishContactIdentitySync, requestEmailChangeVerification, sendPhoneChangeCode } from '../../services/contactIdentityChangeService'
import { subscribePublicProfile } from '../../services/publicProfileService'
import { firestoreDb } from '../../services/firebase'
import { getRaidsCompletedCount, type ReliabilityStats } from '../../services/reliabilityService'
import { useGamification } from '../../context/useGamification'
import { AVATARS, BACKGROUNDS, DEFAULT_UNLOCKS, THEMES } from '../../utils/cosmeticCatalog'
import type { WorkspaceRole } from '../../types/workspace'
import { formatRoleLabel, roleLabelKey } from '../../utils/roleLabels'
import { resolveProfileImageUrl } from '../../utils/profileImage'
import { Select, StatusChip, Textarea, TextInput } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { AppDialog } from '../Common/AppDialog'
import { PhoneNumberInput } from '../Common/PhoneNumberInput'
import { ReliabilityRing } from './ReliabilityRing'
import { switchedOffModulesFor } from '../../utils/workspaceModules'
import { useNls } from '../../nls/nlsProvider'

type ProfileDetailsPageProps = {
  variant?: 'member' | 'helper'
  /** Optional: view another workspace user's profile read-only. When omitted
   * or equal to the current user's uid, this stays in normal self-editing
   * mode. When set to a different uid, ALL edit affordances are hidden and
   * the surface renders the target user's users/{uid} document instead. */
  targetUserId?: string
  onOpenNotificationsSettings?: () => void
}

const fadeInAt = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const, delay: index * 0.06 },
})

export const ProfileDetailsPage = ({ variant = 'member', targetUserId, onOpenNotificationsSettings }: ProfileDetailsPageProps) => {
  const { t } = useNls()
  const { currentUser, userProfile } = useAuth()
  const { preferences: clientPreferences } = useClientPreferences()
  const { preferences: notificationPreferences } = useNotificationPreferences()
  const { activeWorkspaceId, workspaces = [] } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null
  const funPackOff = activeWorkspace ? switchedOffModulesFor(activeWorkspace).has('funPack') : false
  const { profile: gamificationProfile } = useGamification()
  const isSelf = !targetUserId || targetUserId === currentUser?.uid
  const [viewedProfile, setViewedProfile] = useState<UserProfile | null>(null)
  const [viewedLoading, setViewedLoading] = useState(false)
  useEffect(() => {
    if (isSelf || !targetUserId) {
      setViewedProfile(null)
      setViewedLoading(false)
      return undefined
    }
    setViewedLoading(true)
    const stop = subscribePublicProfile(targetUserId, (profile) => {
      setViewedProfile(profile ? ({ id: profile.uid, ...profile } as unknown as UserProfile) : null)
      setViewedLoading(false)
    })
    return () => stop()
  }, [isSelf, targetUserId])
  const displayProfile: UserProfile | null = isSelf ? (userProfile ?? null) : viewedProfile
  const accountRole = normalizeAccountRole(displayProfile?.role)
  const isHelper =
    variant === 'helper' ||
    (isSelf
      ? isHelperOnlyAccount({ userProfile: displayProfile, currentUser, workspaces })
      : isHelperOnlyAccount({ userProfile: displayProfile }))
  const helperInternalId = displayProfile?.helperMetadata?.internalUid || (isSelf ? currentUser?.uid : targetUserId) || ''
  const lockedHelperUsername = helperUsernameFor(helperInternalId)
  const lockedHelperPhone = displayProfile?.helperMetadata?.mobileNumber || displayProfile?.phone || (isSelf ? currentUser?.email : displayProfile?.email) || ''
  const displayName = useDisplayName(displayProfile ?? (isSelf ? currentUser : null))
  const [isEditingState, setIsEditingRaw] = useState(false)
  const isEditing = isEditingState && isSelf
  const setIsEditing = (next: boolean) => {
    if (!isSelf && next) return
    setIsEditingRaw(next)
  }
  const [firstName, setFirstName] = useState(displayProfile?.firstName ?? '')
  const [lastName, setLastName] = useState(displayProfile?.lastName ?? '')
  const [username, setUsername] = useState(isHelper ? lockedHelperUsername : displayProfile?.username ?? '')
  const [phone, setPhone] = useState(isHelper ? lockedHelperPhone : displayProfile?.phone ?? '')
  const [address, setAddress] = useState(displayProfile?.address ?? '')
  const [educationNotes, setEducationNotes] = useState(displayProfile?.educationNotes ?? '')
  const [displayType, setDisplayType] = useState<DisplayPreferenceType>(
    displayProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'),
  )
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [contactDialog, setContactDialog] = useState<null | 'EMAIL' | 'PHONE'>(null)
  const [contactDraft, setContactDraft] = useState('')
  const [verificationId, setVerificationId] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [contactBusy, setContactBusy] = useState(false)
  const [contactMessage, setContactMessage] = useState<string | null>(null)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [imageSource, setImageSource] = useState<'GOOGLE' | 'AVATAR'>(displayProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE')
  const [selectedAvatarId, setSelectedAvatarId] = useState(displayProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0])
  const [imageBusy, setImageBusy] = useState(false)
  const [imageMessage, setImageMessage] = useState<string | null>(null)
  const [reliabilityScore, setReliabilityScore] = useState<number | null>(null)
  const [reliabilityStats, setReliabilityStats] = useState<ReliabilityStats | null>(null)
  const [raidsCompleted, setRaidsCompleted] = useState(0)
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isEditing) return
    setFirstName(displayProfile?.firstName ?? '')
    setLastName(displayProfile?.lastName ?? '')
    setUsername(isHelper ? lockedHelperUsername : displayProfile?.username ?? '')
    setPhone(isHelper ? lockedHelperPhone : displayProfile?.phone ?? '')
    setAddress(displayProfile?.address ?? '')
    setEducationNotes(displayProfile?.educationNotes ?? '')
    setDisplayType(displayProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'))
    setImageSource(displayProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE')
    setSelectedAvatarId(displayProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0])
  }, [gamificationProfile?.equippedAvatarId, isEditing, isHelper, lockedHelperPhone, lockedHelperUsername, displayProfile])

  useEffect(() => {
    if (!isSelf || !currentUser || !activeWorkspaceId) {
      setReliabilityScore(null)
      setReliabilityStats(null)
      setRaidsCompleted(0)
      return undefined
    }
    let cancelled = false
    void getRaidsCompletedCount(activeWorkspaceId, currentUser.uid)
      .then((count) => { if (!cancelled) setRaidsCompleted(count) })
      .catch(() => { if (!cancelled) setRaidsCompleted(0) })
    const stop = onSnapshot(doc(firestoreDb, 'users', currentUser.uid, 'workspaceMemberships', activeWorkspaceId), (snapshot) => {
      const data = snapshot.data()
      setReliabilityScore(typeof data?.reliabilityScore === 'number' ? data.reliabilityScore : null)
      setReliabilityStats(data?.reliabilityStats && typeof data.reliabilityStats === 'object' ? data.reliabilityStats as ReliabilityStats : null)
    })
    return () => {
      cancelled = true
      stop()
    }
  }, [activeWorkspaceId, currentUser, isSelf])

  const normalizedUsername = isHelper ? lockedHelperUsername : normalizeUsername(username)
  const usernameError = validateUsername(normalizedUsername)
  const memberRequiredMissing = !isHelper && !address.trim()
  const computedName = useMemo(() => computeDisplayName({
    firstName,
    lastName,
    username: normalizedUsername,
    phone,
    email: currentUser?.email ?? displayProfile?.email ?? '',
    displayPreferences: { type: displayType },
  }), [currentUser?.email, displayType, firstName, lastName, normalizedUsername, phone, displayProfile?.email])
  const canSave = Boolean(currentUser) && !usernameError && !memberRequiredMissing && !saving
  const displayInitial = (displayName || computedName || 'U').trim().charAt(0).toUpperCase() || 'U'
  const profileImageUrl = resolveProfileImageUrl(displayProfile ?? undefined, isSelf ? currentUser : null)
  const ownedAvatarIds = useMemo(() => new Set([
    ...DEFAULT_UNLOCKS.avatars,
    ...(gamificationProfile?.unlockedAvatarIds ?? []),
    ...(gamificationProfile?.equippedAvatarId ? [gamificationProfile.equippedAvatarId] : []),
  ]), [gamificationProfile?.equippedAvatarId, gamificationProfile?.unlockedAvatarIds])
  const availableAvatars = useMemo(
    () => AVATARS.filter((avatar) => ownedAvatarIds.has(avatar.id)),
    [ownedAvatarIds],
  )
  const reliabilityLabel = reliabilityScore === null ? t('NLSProfileStatusPending') : `${Math.round(reliabilityScore)}/100`
  const reliabilitySummary = reliabilityStats
    ? `${reliabilityStats.successfulScans} scans · ${reliabilityStats.completedChecklistItems + reliabilityStats.completedTasks} done · ${reliabilityStats.vouches} vouches`
    : t('NLSProfileStatusNoSnapshotYet')
  const digestCadenceLabel = clientPreferences.digestCadence === 'DAILY'
    ? t('NLSProfileDigestDaily')
    : t('NLSProfileDigestWeekly')
  const quietMode = notificationPreferences?.quietMode
  const quietHoursLabel = quietMode?.enabled
    ? t('NLSProfileQuietHours', {
        start: String(quietMode.startHour).padStart(2, '0'),
        end: String(quietMode.endHour).padStart(2, '0'),
      })
    : t('NLSProfileQuietOff')

  const displayPreferenceLabel = (type?: DisplayPreferenceType | null) => {
    switch (type) {
      case 'FIRST_LAST':
        return t('NLSProfileDisplayFirstLast')
      case 'USERNAME':
        return t('NLSProfileDisplayUsername')
      case 'PHONE':
        return t('NLSProfileDisplayPhone')
      default:
        return t('NLSProfileStatusNotSet')
    }
  }

  const streakDays = gamificationProfile?.consecutiveLoginStreak ?? 0
  const level = gamificationProfile?.level ?? 1
  const currentXp = gamificationProfile?.currentXp ?? 0
  const xpNeeded = gamificationProfile?.xpNeededForNextLevel ?? 0
  const spinTicketsTotal = (gamificationProfile?.luckySpinTickets ?? 0)
    + (gamificationProfile?.megaSpinTickets ?? 0)
    + (gamificationProfile?.gigaSpinTickets ?? 0)
  const equippedTitleLabel = gamificationProfile?.equippedTitle || 'No title equipped'
  const equippedAvatarLabel = AVATARS.find((avatar) => avatar.id === gamificationProfile?.equippedAvatarId)?.name || 'Default avatar'
  const equippedThemeLabel = THEMES.find((theme) => theme.id === gamificationProfile?.equippedThemeId)?.name || gamificationProfile?.equippedThemeId || t('NLSProfileDefaultTheme')
  const equippedBgLabel = BACKGROUNDS.find((bg) => bg.id === gamificationProfile?.equippedCanvasBgId)?.name || t('NLSProfileDefaultCanvas')
  const formatLevelXp = (lvl: number, cur: number, needed: number) => {
    return t('NLSProfileLevelXpFormat', {
      level: String(lvl),
      current: String(cur),
      needed: String(needed),
    })
  }
  const cosmeticInventoryCount = (gamificationProfile?.unlockedAvatarIds?.length ?? 0)
    + (gamificationProfile?.unlockedThemeIds?.length ?? 0)
    + (gamificationProfile?.unlockedBackgroundIds?.length ?? 0)
    + (gamificationProfile?.unlockedTitlePrefixes?.length ?? 0)
    + (gamificationProfile?.unlockedTitleSuffixes?.length ?? 0)

  if (!currentUser) return null
  if (!isSelf) {
    if (viewedLoading) return <div className="p-8 text-center text-sm text-[var(--muted)]">{t('NLSProfileStatusLoading')}</div>
    if (!displayProfile) return <div className="p-8 text-center text-sm text-[var(--muted)]">{t('NLSProfileStatusUnavailable')}</div>
  }

  const resetDraft = () => {
    setFirstName(displayProfile?.firstName ?? '')
    setLastName(displayProfile?.lastName ?? '')
    setUsername(isHelper ? lockedHelperUsername : displayProfile?.username ?? '')
    setPhone(isHelper ? lockedHelperPhone : displayProfile?.phone ?? '')
    setAddress(displayProfile?.address ?? '')
    setEducationNotes(displayProfile?.educationNotes ?? '')
    setDisplayType(displayProfile?.displayPreferences?.type ?? (isHelper ? 'PHONE' : 'FIRST_LAST'))
    setMessage(null)
    setIsEditing(false)
  }

  const openContactDialog = (mode: 'EMAIL' | 'PHONE') => {
    if (!isSelf) return
    setContactDialog(mode)
    setContactDraft(mode === 'EMAIL' ? currentUser.email ?? displayProfile?.email ?? '' : displayProfile?.helperMetadata?.mobileNumber || displayProfile?.phone || '')
    setVerificationId('')
    setVerificationCode('')
    setContactMessage(null)
  }

  const closeContactDialog = () => {
    if (contactBusy) return
    setContactDialog(null)
    setContactMessage(null)
    setVerificationId('')
    setVerificationCode('')
  }

  const openImageDialog = () => {
    if (!isSelf) return
    setImageSource(displayProfile?.profileImage?.source === 'AVATAR' ? 'AVATAR' : 'GOOGLE')
    setSelectedAvatarId(displayProfile?.profileImage?.avatarId || gamificationProfile?.equippedAvatarId || DEFAULT_UNLOCKS.avatars[0])
    setImageMessage(null)
    setImageDialogOpen(true)
  }

  const saveProfileImage = async () => {
    setImageBusy(true)
    setImageMessage(null)
    try {
      await updateUserProfileImage(imageSource === 'GOOGLE' ? { source: 'GOOGLE' } : { source: 'AVATAR', avatarId: selectedAvatarId })
      setImageDialogOpen(false)
    } catch (error) {
      setImageMessage(error instanceof Error ? error.message : t('NLSProfileStatusImageCouldNotUpdate'))
    } finally {
      setImageBusy(false)
    }
  }

  const sendContactChange = async () => {
    if (!contactDialog) return
    setContactBusy(true)
    setContactMessage(null)
    try {
      if (contactDialog === 'EMAIL') {
        await requestEmailChangeVerification(contactDraft)
        setContactMessage('Verification sent. Open the link from the new inbox, then return here and finish sync.')
      } else {
        if (Capacitor.isNativePlatform()) {
          throw new Error('Phone change from the native app needs the native OTP update path. Use the web console for this change until that path is enabled.')
        }
        if (!recaptchaContainerRef.current) throw new Error('Verification surface was not ready. Reopen this dialog.')
        const nextVerificationId = await sendPhoneChangeCode(contactDraft, recaptchaContainerRef.current)
        setVerificationId(nextVerificationId)
        setContactMessage('SMS code sent. Enter it here to update the phone number.')
      }
    } catch (error) {
      setContactMessage(error instanceof Error ? error.message : 'Contact change could not start.')
    } finally {
      setContactBusy(false)
    }
  }

  const finishEmailSync = async () => {
    setContactBusy(true)
    setContactMessage(null)
    try {
      await finishContactIdentitySync()
      setContactMessage('Contact identity synced.')
      setContactDialog(null)
    } catch (error) {
      setContactMessage(error instanceof Error ? error.message : 'Sync could not finish yet. Verify the new email link first, then try again.')
    } finally {
      setContactBusy(false)
    }
  }

  const confirmPhoneChange = async () => {
    if (!verificationId) return
    setContactBusy(true)
    setContactMessage(null)
    try {
      await confirmPhoneChangeCode(verificationId, verificationCode)
      setContactMessage('Phone number updated.')
      setContactDialog(null)
    } catch (error) {
      setContactMessage(error instanceof Error ? error.message : 'Phone number could not be updated.')
    } finally {
      setContactBusy(false)
    }
  }

  const save = async () => {
    if (!isSelf) return
    if (!canSave) return
    setSaving(true)
    setMessage(null)
    try {
      await updateUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        username: normalizedUsername,
        phone: isHelper ? lockedHelperPhone : phone.trim(),
        address: isHelper ? undefined : address.trim(),
        educationNotes: isHelper ? undefined : educationNotes.trim(),
        displayType,
      })
      setMessage(t('NLSProfileStatusSaved'))
      setIsEditing(false)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('NLSProfileStatusCouldNotSave'))
    } finally {
      setSaving(false)
    }
  }

  const digestSettingsPill = isSelf ? (
    <Button
      variant="tertiary"
      size="md"
      className="profileDigestSettingsPill"
      onClick={onOpenNotificationsSettings}
      disabled={!onOpenNotificationsSettings}
      title={onOpenNotificationsSettings ? 'Change digest preferences in Settings' : undefined}
    >
      <ForumRoundedIcon fontSize="inherit" className="profileDigestSettingsPill__icon" />
      <span>{t('NLSProfileDigestLabel')} · {digestCadenceLabel} · {quietHoursLabel}</span>
      <span className="profileDigestSettingsPill__sep">·</span>
      <strong>{t('NLSProfileChangeInSettings')}</strong>
    </Button>
  ) : null

  // ---------------------------------------------------------------------
  // Shared dialogs (used by both variants) — kept identical
  // ---------------------------------------------------------------------
  const contactDialogNode = (
    <AppDialog
      open={Boolean(contactDialog)}
      title={contactDialog === 'PHONE' ? 'Change phone number' : 'Change email'}
      onDismiss={closeContactDialog}
      message={(
        <div className="profileContactDialogBody">
          <p>Username <strong>{normalizedUsername}</strong> stays fixed and anchors the account while the verified contact detail changes.</p>
          {contactDialog === 'EMAIL' ? (
            <>
              <label>
                <span>New email</span>
                <TextInput type="email" value={contactDraft} onChange={setContactDraft} disabled={contactBusy} />
              </label>
              <p className="profileContactDialogNote">A verification link is sent first. Firebase updates Auth only after the new inbox confirms it; then the app syncs profile and workspace membership records.</p>
            </>
          ) : (
            <>
              <label>
                <span>New phone number</span>
                <PhoneNumberInput value={contactDraft} onChange={setContactDraft} disabled={contactBusy || Boolean(verificationId)} groupClassName="profilePhoneInputGroup" testIdPrefix="profile-change-phone" />
              </label>
              {verificationId ? (
                <label>
                  <span>SMS code</span>
                  <TextInput inputMode="numeric" value={verificationCode} onChange={(next) => setVerificationCode(next.replace(/\D/g, '').slice(0, 6))} disabled={contactBusy} />
                </label>
              ) : null}
              <div ref={recaptchaContainerRef} />
            </>
          )}
          {contactMessage ? <p className="profileDetailsMessage">{contactMessage}</p> : null}
        </div>
      )}
      actions={[
        { label: 'Cancel', variant: 'ghost', onClick: closeContactDialog, disabled: contactBusy },
        ...(contactDialog === 'EMAIL'
          ? [
              { label: contactMessage?.startsWith('Verification sent') ? 'Finish sync' : 'Send verification', variant: 'primary' as const, onClick: contactMessage?.startsWith('Verification sent') ? finishEmailSync : sendContactChange, disabled: contactBusy || !contactDraft.trim(), loading: contactBusy },
            ]
          : [
              { label: verificationId ? 'Confirm phone' : 'Send code', variant: 'primary' as const, onClick: verificationId ? confirmPhoneChange : sendContactChange, disabled: contactBusy || !contactDraft.trim() || (Boolean(verificationId) && verificationCode.length < 6), loading: contactBusy },
            ]),
      ]}
    />
  )

  const imageDialogNode = (
    <AppDialog
      open={imageDialogOpen}
      title="Change profile picture"
      onDismiss={() => { if (!imageBusy) setImageDialogOpen(false) }}
      message={(
        <div className="profileImageDialogBody">
          <div className="profileImagePreview" aria-hidden="true">
            {imageSource === 'GOOGLE' && currentUser.photoURL
              ? <img src={currentUser.photoURL} alt="" />
              : <img src={resolveProfileImageUrl({ profileImage: { source: 'AVATAR', avatarId: selectedAvatarId } })} alt="" />}
          </div>
          <div className="profileImageSourceGrid">
            <Pressable
              aria-pressed={imageSource === 'GOOGLE'}
              className={`profileImageSourceCard ${imageSource === 'GOOGLE' ? 'isActive' : ''}`}
              onClick={() => setImageSource('GOOGLE')}
              disabled={imageBusy || !currentUser.photoURL}
            >
              <strong>Google image</strong>
              <span>{currentUser.photoURL ? 'Sync from your Google/Gmail account.' : 'No Google image is available.'}</span>
            </Pressable>
            <Pressable
              aria-pressed={imageSource === 'AVATAR'}
              className={`profileImageSourceCard ${imageSource === 'AVATAR' ? 'isActive' : ''}`}
              onClick={() => setImageSource('AVATAR')}
              disabled={imageBusy || availableAvatars.length === 0}
            >
              <strong>Timelining avatar</strong>
              <span>Use one of your available avatars.</span>
            </Pressable>
          </div>
          {imageSource === 'AVATAR' ? (
            <div className="profileAvatarChoiceGrid">
              {availableAvatars.map((avatar) => (
                <Pressable
                  key={avatar.id}
                  aria-pressed={selectedAvatarId === avatar.id}
                  className={`profileAvatarChoice ${selectedAvatarId === avatar.id ? 'isActive' : ''}`}
                  onClick={() => setSelectedAvatarId(avatar.id)}
                  disabled={imageBusy}
                >
                  <span aria-hidden="true">{avatar.emoji}</span>
                  <strong>{avatar.name}</strong>
                </Pressable>
              ))}
            </div>
          ) : null}
          {imageMessage ? <p className="profileDetailsMessage">{imageMessage}</p> : null}
        </div>
      )}
      actions={[
        { label: 'Cancel', variant: 'ghost', onClick: () => setImageDialogOpen(false), disabled: imageBusy },
        { label: 'Use this image', variant: 'primary', onClick: saveProfileImage, disabled: imageBusy || (imageSource === 'GOOGLE' && !currentUser.photoURL), loading: imageBusy },
      ]}
    />
  )

  // ---------------------------------------------------------------------
  // Helper variant — keep the current simpler surface (unchanged UX)
  // ---------------------------------------------------------------------
  if (isHelper) {
    return (
      <section className="profileDetailsPanel">
        <div className="profileDetailsHero">
          <Pressable className="profileDetailsAvatar profileDetailsAvatarButton" onClick={openImageDialog} aria-label={t('NLSProfileAriaChangePicture')}>
            {profileImageUrl ? <img src={profileImageUrl} alt="" /> : displayInitial}
          </Pressable>
          <div className="profileDetailsHeroCopy">
            <span className="settingsEyebrow">{t('NLSProfileHelperProfile')}</span>
            <h2>{displayName}</h2>
            <p>{t('NLSProfileHelperProfileBody')}</p>
            <div className="profileDetailsHeroMeta" aria-label={t('NLSProfileAriaSummary')}>
              <span>{accountRole}</span>
              <span>{reliabilityLabel}</span>
              <span>{raidsCompleted} raids</span>
            </div>
          </div>
          {!isSelf ? null : isEditing ? (
            <div className="profileDetailsActions">
              <Button variant="tertiary" size="sm" disabled={saving} onClick={openImageDialog}>{t('NLSProfileActionPicture')}</Button>
              <Button variant="tertiary" size="sm" disabled={saving} onClick={resetDraft}>{t('NLSCommonActionCancel')}</Button>
              <Button variant="tertiary" size="md" disabled={!canSave} onClick={() => void save()}>
                {saving ? t('NLSProfileActionSaving') : t('NLSProfileActionSave')}
              </Button>
            </div>
          ) : (
            <div className="profileDetailsActions">
              <Button variant="tertiary" size="sm" onClick={openImageDialog}>{t('NLSProfileActionChangePicture')}</Button>
              <Button variant="tertiary" size="sm" className="profileDetailsEditButton" onClick={() => setIsEditing(true)}>{t('NLSProfileActionEditProfile')}</Button>
            </div>
          )}
        </div>

        {!isEditing ? (
          <div className="profileDetailsReadout" aria-label={t('NLSProfileAriaDetails')}>
            <div className="profileDetailsStat"><span>{t('NLSProfileLabelDisplayName')}</span><strong>{displayName}</strong></div>
            <div className="profileDetailsStat"><span>{t('NLSProfileLabelReliabilityBadge')}</span><strong>{reliabilityLabel}</strong></div>
            <div className="profileDetailsStat"><span>{t('NLSProfileLabelRaidsCompleted')}</span><strong>{raidsCompleted}</strong></div>
            {!funPackOff && (
              <>
                <div className="profileDetailsStat"><span>{t('NLSProfileLabelLoginStreak')}</span><strong data-testid="profile-streak-days">{streakDays} day{streakDays === 1 ? '' : 's'}</strong></div>
                <div className="profileDetailsStat"><span>{t('NLSProfileLabelLevelXp')}</span><strong>{formatLevelXp(level, currentXp, xpNeeded)}</strong></div>
                <div className="profileDetailsStat"><span>{t('NLSProfileLabelSpinTickets')}</span><strong>{spinTicketsTotal}</strong></div>
              </>
            )}
            <div className="profileDetailsStat profileDetailsStat--wide"><span>{t('NLSProfileLabelReliabilitySignals')}</span><strong>{reliabilitySummary}</strong></div>
            <div className="profileDetailsStat profileDetailsStat--wide" data-testid="profile-cosmetics-row">
              <span>{t('NLSProfileLabelEquippedCosmetics')}</span>
              <strong>{equippedAvatarLabel} · {equippedTitleLabel} · {equippedThemeLabel} · {equippedBgLabel} · {cosmeticInventoryCount} in inventory</strong>
            </div>
            <div><span>{t('NLSProfileLabelUsername')}</span><strong>{displayProfile?.username || normalizedUsername || t('NLSProfileStatusNotSet')}</strong></div>
            {isSelf ? (
              <div className="profileDetailsContactRow">
                <span>{t('NLSProfileLabelMobileNumber')}</span>
                <strong>{displayProfile?.helperMetadata?.mobileNumber || displayProfile?.phone || t('NLSProfileStatusNotSet')}</strong>
                <Button variant="tertiary" size="sm" onClick={() => openContactDialog('PHONE')}>{t('NLSProfileActionChange')}</Button>
              </div>
            ) : null}
            <div><span>{t('NLSProfileLabelInternalId')}</span><strong>{helperInternalId || t('NLSProfileStatusNotSet')}</strong></div>
            {isSelf ? <div><span>{t('NLSProfileLabelDisplayPreference')}</span><strong>{displayPreferenceLabel(displayProfile?.displayPreferences?.type ?? displayType)}</strong></div> : null}
          </div>
        ) : (
          <>
            <div className="profileOnboardingGrid profileDetailsGrid">
              <label><span>{t('NLSProfileLabelFirstName')}</span><TextInput block value={firstName} onChange={setFirstName} autoComplete="given-name" /></label>
              <label><span>{t('NLSProfileLabelLastName')}</span><TextInput block value={lastName} onChange={setLastName} autoComplete="family-name" /></label>
              <label>
                <span>{t('NLSProfileLabelUsername')}</span>
                <TextInput block value={normalizedUsername} disabled readOnly autoComplete="username" />
                <small>{t('NLSProfileHintUsernamePermanent')}</small>
              </label>
              <label>
                <span className="profileDetailsFieldHeader">{t('NLSProfileLabelMobileNumber')} {isSelf ? <Button variant="tertiary" size="sm" onClick={() => openContactDialog('PHONE')}>{t('NLSProfileActionChange')}</Button> : null}</span>
                <PhoneNumberInput
                  value={phone}
                  onChange={setPhone}
                  disabled
                  groupClassName="profilePhoneInputGroup"
                  testIdPrefix="profile-details-phone"
                />
                <small>{t('NLSProfileHintContactLinked')}</small>
              </label>
              <label>
                <span>{t('NLSProfileLabelDisplayAs')}</span>
                <Select<DisplayPreferenceType>
                  ariaLabel={t('NLSProfileLabelDisplayPreference')}
                  value={displayType}
                  options={[{ value: 'FIRST_LAST', label: t('NLSProfileDisplayFirstLast') }, { value: 'USERNAME', label: t('NLSProfileDisplayUsername') }, { value: 'PHONE', label: t('NLSProfileDisplayPhone') }]}
                  onChange={setDisplayType}
                  trigger="pill"
                />
              </label>
            </div>
            <section className="profileOnboardingPreview">
              <span>{t('NLSProfileLabelComputedName')}</span>
              <strong>{computedName}</strong>
              <small>{t('NLSProfileRoleLabel', { role: formatRoleLabel(t, accountRole) })}</small>
            </section>
            {usernameError && <p className="authError">{usernameError}</p>}
          </>
        )}
        {message && <p className="profileDetailsMessage">{message}</p>}
        {contactDialogNode}
        {imageDialogNode}
        {isSelf ? (
        <div className="profileDetailsSection">
          {digestSettingsPill}
        </div>
        ) : null}
      </section>
    )
  }

  // ---------------------------------------------------------------------
  // Member variant — new Identity Console (deckV2)
  // ---------------------------------------------------------------------
  return (
    <section className="gTabBody deckV2" aria-label={t('NLSProfileAriaDetails')}>
      <motion.section className="deckHero" {...fadeInAt(0)}>
        <div className="deckHeroInner">
          <Pressable
            className="deckHeroPortrait"
            onClick={openImageDialog}
            aria-label={t('NLSProfileAriaChangePicture')}
          >
            <span className="deckHeroPortraitInner">
              {profileImageUrl ? <img src={profileImageUrl} alt="" /> : displayInitial}
            </span>
          </Pressable>
          <div className="deckHeroCore">
            <span className="deckHeroEyebrow">{t('NLSProfileIdentityConsole')}</span>
            <h2 className="deckHeroName">{displayName}</h2>
            {equippedTitleLabel && equippedTitleLabel !== 'No title equipped' ? (
              <span className="deckHeroTitle" data-title={equippedTitleLabel} aria-label={`Equipped title ${equippedTitleLabel}`}>
                {equippedTitleLabel}
              </span>
            ) : null}
            <div className="deckHeroChipRow">
              <span className="deckChip deckChip--role">
                <span className="deckChipIcon"><BadgeRoundedIcon fontSize="inherit" /></span>
                {t(roleLabelKey(accountRole as WorkspaceRole))}
              </span>
              {!funPackOff && (
                <>
                  <span className="deckChip deckChip--streak" title="Consecutive-day login streak">
                    <span className="deckChipIcon"><LocalFireDepartmentRoundedIcon fontSize="inherit" /></span>
                    <span data-testid="profile-streak-days">{streakDays} day{streakDays === 1 ? '' : 's'}</span>
                  </span>
                  <StatusChip size="sm" tone="neutral" icon={<TrendingUpRoundedIcon fontSize="inherit" />}>
                    {formatLevelXp(level, currentXp, xpNeeded)}
                  </StatusChip>
                  <span className="deckChip deckChip--credits">
                    <span className="deckChipIcon"><ConfirmationNumberRoundedIcon fontSize="inherit" /></span>
                    {spinTicketsTotal} {spinTicketsTotal === 1 ? t('NLSProfileTicketSingular') : t('NLSProfileTicketPlural')}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="deckHeroAside">
            <div className="deckHeroAsideActions">
              {!isSelf ? null : isEditing ? (
                <>
                  <Button variant="tertiary" size="sm" disabled={saving} onClick={openImageDialog}>{t('NLSProfileActionPicture')}</Button>
                  <Button variant="tertiary" size="sm" disabled={saving} onClick={resetDraft}>{t('NLSCommonActionCancel')}</Button>
                  <Button variant="primary" size="sm" disabled={!canSave} onClick={() => void save()}>
                    {saving ? t('NLSProfileActionSaving') : t('NLSProfileActionSave')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="tertiary" size="sm" onClick={openImageDialog}>{t('NLSProfileActionChangePicture')}</Button>
                  <Button variant="primary" size="sm" className="profileDetailsEditButton" onClick={() => setIsEditing(true)}>{t('NLSProfileActionEditProfile')}</Button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {!isEditing ? (
        <>
          <motion.section className="deckSection" {...fadeInAt(1)}>
            <div className="deckSectionHead">
              <div className="deckSectionHeadCopy">
                <h3 className="deckSectionTitle">Reputation</h3>
                <span className="deckSectionSub">Reliability signals collected across your workspaces.</span>
              </div>
            </div>
            <div className="deckMetricGrid">
              <div className="deckMetric is-featured">
                <ReliabilityRing score={reliabilityScore} label="Reliability" />
                <div className="deckMetricFeaturedBody">
                  <span className="deckMetricLabel">{t('NLSProfileLabelReliabilityBadge')}</span>
                  <strong>{reliabilityLabel}</strong>
                  <span className="deckMetricSub">{reliabilitySummary}</span>
                </div>
              </div>
              <div className="deckMetric">
                <span className="deckMetricLabel"><EmojiEventsRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelRaidsCompleted')}</span>
                <span className="deckMetricValue">{raidsCompleted}</span>
              </div>
              {!funPackOff && (
                <>
                  <div className="deckMetric">
                    <span className="deckMetricLabel"><LocalFireDepartmentRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelLoginStreak')}</span>
                    <span className="deckMetricValue">{streakDays}<small>day{streakDays === 1 ? '' : 's'}</small></span>
                  </div>
                  <div className="deckMetric">
                    <span className="deckMetricLabel"><TrendingUpRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelLevelXp')}</span>
                    <span className="deckMetricValue">{formatLevelXp(level, currentXp, xpNeeded)}</span>
                  </div>
                  <div className="deckMetric">
                    <span className="deckMetricLabel"><ConfirmationNumberRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelSpinTickets')}</span>
                    <span className="deckMetricValue">{spinTicketsTotal}</span>
                  </div>
                </>
              )}
              <div className="deckMetric deckMetric--wide" data-testid="profile-cosmetics-row">
                <span className="deckMetricLabel"><VerifiedRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelEquippedCosmetics')}</span>
                <span className="deckMetricSub">
                  {equippedAvatarLabel} · {equippedTitleLabel} · {equippedThemeLabel} · {equippedBgLabel} · {cosmeticInventoryCount} in inventory
                </span>
              </div>
            </div>
          </motion.section>

          <motion.section className="deckSection" {...fadeInAt(2)}>
            <div className="deckSectionHead">
              <div className="deckSectionHeadCopy">
                <h3 className="deckSectionTitle">{t('NLSProfileSectionPresenceContact')}</h3>
                <span className="deckSectionSub">How the app addresses you and reaches you.</span>
              </div>
            </div>
            <div className="deckCardGrid">
              <div className="deckCard">
                <div className="deckCardHead">
                  <span className="deckCardLabel"><PersonRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelUsername')}</span>
                </div>
                <span className="deckCardValue">{displayProfile?.username || normalizedUsername || t('NLSProfileStatusNotSet')}</span>
              </div>
              {isSelf ? (
                <div className="deckCard">
                  <div className="deckCardHead">
                    <span className="deckCardLabel"><PhoneRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelMobileNumber')}</span>
                    <Button variant="tertiary" size="sm" onClick={() => openContactDialog('PHONE')}>{t('NLSProfileActionChange')}</Button>
                  </div>
                  <span className="deckCardValue">{displayProfile?.helperMetadata?.mobileNumber || displayProfile?.phone || t('NLSProfileStatusNotSet')}</span>
                </div>
              ) : null}
              {isSelf ? (
                <div className="deckCard">
                  <div className="deckCardHead">
                    <span className="deckCardLabel"><EmailRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelEmail')}</span>
                    <Button variant="tertiary" size="sm" onClick={() => openContactDialog('EMAIL')}>{t('NLSProfileActionChange')}</Button>
                  </div>
                  <span className="deckCardValue">{currentUser.email ?? displayProfile?.email ?? t('NLSProfileStatusNotSet')}</span>
                </div>
              ) : null}
              {isSelf ? (
              <div className="deckCard">
                <div className="deckCardHead">
                  <span className="deckCardLabel"><WorkspacesRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelDisplayPreference')}</span>
                </div>
                <span className="deckCardValue">{displayPreferenceLabel(displayProfile?.displayPreferences?.type ?? displayType)}</span>
              </div>
              ) : null}
            </div>
          </motion.section>

          {isSelf ? (
            <motion.section className="deckSection" {...fadeInAt(3)}>
              <div className="deckSectionHead">
                <div className="deckSectionHeadCopy">
                  <h3 className="deckSectionTitle">{t('NLSProfileSectionPersonalMetadata')}</h3>
                  <span className="deckSectionSub">Address, notes, and things the team may need on file.</span>
                </div>
              </div>
              <div className="deckCardGrid">
                <div className="deckCard deckCard--wide">
                  <div className="deckCardHead">
                    <span className="deckCardLabel"><HomeRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelAddress')}</span>
                  </div>
                  <span className={`deckCardValue ${displayProfile?.address ? '' : 'deckCardValue--muted'}`}>{displayProfile?.address || t('NLSProfileStatusNotSet')}</span>
                </div>
                <div className="deckCard deckCard--wide">
                  <div className="deckCardHead">
                    <span className="deckCardLabel"><SchoolRoundedIcon fontSize="inherit" /> {t('NLSProfileLabelEducationNotes')}</span>
                  </div>
                  <span className={`deckCardValue ${displayProfile?.educationNotes ? '' : 'deckCardValue--muted'}`}>{displayProfile?.educationNotes || t('NLSProfileStatusNotSet')}</span>
                </div>
              </div>
            </motion.section>
          ) : null}

          <motion.section className="deckSection" {...fadeInAt(4)}>
            <div className="deckSectionHead">
              <div className="deckSectionHeadCopy">
                <h3 className="deckSectionTitle">Communications</h3>
                <span className="deckSectionSub"><ForumRoundedIcon fontSize="inherit" /> Digest cadence and notification preferences.</span>
              </div>
            </div>
            {isSelf ? (
            <div className="deckCommsFrame">
              {digestSettingsPill}
            </div>
            ) : null}
          </motion.section>
        </>
      ) : (
        <motion.section className="deckSection" {...fadeInAt(1)}>
          <div className="deckSectionHead">
            <div className="deckSectionHeadCopy">
              <h3 className="deckSectionTitle">Edit Identity</h3>
              <span className="deckSectionSub">Update the fields the app uses to address you. Save to publish.</span>
            </div>
          </div>
          <div className="profileOnboardingGrid profileDetailsGrid">
            <label><span>{t('NLSProfileLabelFirstName')}</span><TextInput block value={firstName} onChange={setFirstName} autoComplete="given-name" /></label>
            <label><span>{t('NLSProfileLabelLastName')}</span><TextInput block value={lastName} onChange={setLastName} autoComplete="family-name" /></label>
            <label>
              <span>{t('NLSProfileLabelUsername')}</span>
              <TextInput block value={normalizedUsername} disabled readOnly autoComplete="username" />
              <small>{t('NLSProfileHintUsernamePermanent')}</small>
            </label>
            <label>
              <span className="profileDetailsFieldHeader">{t('NLSProfileLabelMobileNumber')} {isSelf ? <Button variant="tertiary" size="sm" onClick={() => openContactDialog('PHONE')}>{t('NLSProfileActionChange')}</Button> : null}</span>
              <PhoneNumberInput
                value={phone}
                onChange={setPhone}
                disabled
                groupClassName="profilePhoneInputGroup"
                testIdPrefix="profile-details-phone"
              />
              <small>{t('NLSProfileHintContactLinked')}</small>
            </label>
            <label>
              <span className="profileDetailsFieldHeader">{t('NLSProfileLabelEmail')} {isSelf ? <Button variant="tertiary" size="sm" onClick={() => openContactDialog('EMAIL')}>{t('NLSProfileActionChange')}</Button> : null}</span>
              <TextInput block value={currentUser.email ?? displayProfile?.email ?? ''} disabled />
            </label>
            <label>
              <span>{t('NLSProfileLabelAddress')}</span>
              <TextInput block value={address} onChange={setAddress} autoComplete="street-address" />
            </label>
            <label className="profileDetailsWide">
              <span>{t('NLSProfileLabelEducationNotes')}</span>
              <Textarea block value={educationNotes} onChange={setEducationNotes} rows={3} />
            </label>
            <label>
              <span>{t('NLSProfileLabelDisplayAs')}</span>
              <Select<DisplayPreferenceType>
                ariaLabel={t('NLSProfileLabelDisplayPreference')}
                value={displayType}
                options={[{ value: 'FIRST_LAST', label: t('NLSProfileDisplayFirstLast') }, { value: 'USERNAME', label: t('NLSProfileDisplayUsername') }, { value: 'PHONE', label: t('NLSProfileDisplayPhone') }]}
                onChange={setDisplayType}
                trigger="pill"
              />
            </label>
          </div>
          <section className="profileOnboardingPreview">
            <span>{t('NLSProfileLabelComputedName')}</span>
            <strong>{computedName}</strong>
            <small>{t('NLSProfileRoleLabel', { role: formatRoleLabel(t, accountRole) })}</small>
          </section>
          {usernameError && <p className="authError">{usernameError}</p>}
          {memberRequiredMissing && <p className="authError">{t('NLSProfileErrorAddressRequired')}</p>}
        </motion.section>
      )}

      {message && <p className="deckMessage">{message}</p>}
      {contactDialogNode}
      {imageDialogNode}
    </section>
  )
}
